import { ImagePlus, Loader2, MonitorPlay, RotateCcw } from "lucide-react";
import { useT } from "@/i18n";
import SpeakButton from "@/components/SpeakButton";
import useImageGen from "@/components/ailab/useImageGen";
import { STORY_TEXT } from "@/components/story/content";

export const scriptText = (script) => [script.title, ...script.scenes.map((s) => s.text)].filter(Boolean).join("\n\n");

export default function ScriptEditor({ script, setScript, cover, setCover, onRead, onRestart }) {
  const { lang } = useT();
  const tx = STORY_TEXT[lang];
  const { generate, loading } = useImageGen(tx.imgErr, lang);
  const setScene = (i, text) => setScript({ ...script, scenes: script.scenes.map((s, j) => (j === i ? { ...s, text } : s)) });
  const makeCover = async () => {
    const img = await generate(`Colorful kid-friendly cartoon cover illustration, no text: ${script.coverPrompt}`.slice(0, 600), null, null, script.title);
    if (img) setCover(img);
  };

  return (
    <div className="bg-white rounded-[2rem] shadow-lg p-5 sm:p-7" data-testid="story-script">
      <p className="text-xs font-bold uppercase tracking-wider text-teal-700">{tx.script}</p>
      <input value={script.title} onChange={(e) => setScript({ ...script, title: e.target.value.slice(0, 80) })}
        className="w-full font-fredoka text-2xl sm:text-3xl font-bold text-blue-950 mt-1 outline-none border-b-4 border-transparent focus:border-teal-300" data-testid="story-title-input" />
      <p className="text-sm font-semibold text-blue-900/70 mt-1">{tx.edit}</p>
      <div className="mt-5 grid lg:grid-cols-[1fr_16rem] gap-6">
        <ol className="space-y-4">
          {script.scenes.map((s, i) => (
            <li key={i} className="bg-teal-50 rounded-2xl p-4" data-testid={`story-scene-${i}`}>
              <p className="font-fredoka font-bold text-teal-800">{i + 1}. {s.label}</p>
              <textarea value={s.text} onChange={(e) => setScene(i, e.target.value.slice(0, 600))} rows={3}
                className="mt-2 w-full bg-white rounded-xl p-3 font-semibold text-blue-950 outline-none border-2 border-teal-100 focus:border-teal-400" data-testid={`story-scene-text-${i}`} />
            </li>
          ))}
        </ol>
        <div className="space-y-3">
          <div className="aspect-square rounded-2xl bg-gradient-to-br from-teal-200 to-sky-300 overflow-hidden flex items-center justify-center" data-testid="story-cover-box">
            {cover ? <img src={cover} alt="" className="w-full h-full object-cover" data-testid="story-cover-img" /> : loading ? <Loader2 className="w-10 h-10 animate-spin text-white" /> : <img src="/img/story.webp" alt="" className="w-2/3 opacity-80" />}
          </div>
          <button type="button" onClick={makeCover} disabled={loading} className="w-full glossy-btn bg-violet-500 text-white font-fredoka font-bold px-4 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="story-cover-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />} {loading ? tx.coverLoading : cover ? tx.coverAgain : tx.cover}
          </button>
          <SpeakButton key={`script-${lang}`} mascot="lumi" text={scriptText(script)} testId="story-speak-lumi" />
        </div>
      </div>
      <p className="mt-5 text-sm font-bold text-amber-700">{tx.notSaved}</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" onClick={onRead} className="glossy-btn bg-orange-500 text-white font-fredoka font-bold px-6 py-3 rounded-full inline-flex items-center gap-2" data-testid="story-to-prompter-btn">
          <MonitorPlay className="w-5 h-5" /> {tx.toPrompter}
        </button>
        <button type="button" onClick={onRestart} className="inline-flex items-center gap-2 font-bold text-blue-900 px-4" data-testid="story-restart-btn">
          <RotateCcw className="w-4 h-4" /> {tx.restart}
        </button>
      </div>
    </div>
  );
}
